import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from '../../../../common/Loading';
const VERIFICATION_CHANNELS = [
    { key: 'email', label: 'Email', icon: 'bi-envelope', tone: 'email', sendType: 'EMAIL' },
    { key: 'sms', label: 'SMS', icon: 'bi-chat-left-text', tone: 'sms', sendType: 'SMS' },
    { key: 'phone', label: 'Phone', icon: 'bi-phone', tone: 'phone', sendType: 'CALL' },
];

const isVerified = (value) => value === true || value === 1 || value === '1';

function VerificationCard() {
    const [clinicSettings, setClinicSettings] = useState({});
    const { register, handleSubmit, reset } = useForm();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const { clinicId } = useParams();

    useEffect(() => {
        loadVerification();
    }, [clinicId]);

    const applyVerificationState = (verification = {}) => {
        setClinicSettings(verification);
        reset({
            email: isVerified(verification.email),
            sms: isVerified(verification.sms),
            phone: isVerified(verification.phone),
        });
    };

    const loadVerification = () => {
        setIsLoading(true);
        setError(null);
        clinicService
            .getClinicById(clinicId)
            .then((res) => {
                applyVerificationState(res.data.verification || {});
            })
            .catch(() => {
                setError('Failed to load verification settings.');
                toast.error('Error while getting clinic info', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const sendVerification = (verificationType) => {
        clinicService
            .sendVerification(clinicId, verificationType)
            .then(() => {
                toast.success(`${verificationType} verification request sent`, { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .catch(() => {
                toast.error('Error while sending verification request', { position: toast.POSITION.BOTTOM_RIGHT });
            });
    };

    const onSubmit = (data) => {
        setIsSaving(true);
        const payload = {
            clinicId,
            email: !!data.email,
            sms: !!data.sms,
            phone: !!data.phone,
        };

        clinicService
            .updateVerification(clinicId, payload)
            .then((res) => {
                applyVerificationState(res.data || {});
                setIsEditing(false);
                toast.success('Verification settings updated', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .catch(() => {
                toast.error('Error while updating verification', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const verifiedCount = VERIFICATION_CHANNELS.filter((ch) => isVerified(clinicSettings[ch.key])).length;

    const handleCancelEdit = () => {
        setIsEditing(false);
        applyVerificationState(clinicSettings);
    };

    return (
        <div className="col-lg-4 clinic-verification-panel">
            <div className="mb-3">
                <h5 className="clinic-verification__title mb-1">
                    <i className="bi bi-shield-check text-primary me-1"></i>
                    Verification
                </h5>
                <p className="text-muted small mb-0">Email, SMS, and phone verification status for this clinic.</p>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-patch-check"></i>
                        Channels
                    </span>
                    {!isLoading && !isEditing && (
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setIsEditing(true)}>
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                        </button>
                    )}
                    {isEditing && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleCancelEdit} disabled={isSaving}>
                            <i className="bi bi-x-lg me-1"></i>
                            Cancel
                        </button>
                    )}
                </div>

                <div className="notifications-panel__body">
                    {error && !isLoading && (
                        <div className="alert alert-warning border-0 py-2 px-3 small mb-3">{error}</div>
                    )}

                    {isLoading ? (
                        <Loading />
                    ) : isEditing ? (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {VERIFICATION_CHANNELS.map((channel) => (
                                <div key={channel.key} className="clinic-verification-row">
                                    <span className="clinic-verification-row__label">
                                        <span className={`clinic-verification-row__icon clinic-verification-row__icon--${channel.tone}`}>
                                            <i className={`bi ${channel.icon}`}></i>
                                        </span>
                                        {channel.label}
                                    </span>
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`verify-${channel.key}`}
                                            disabled={isSaving}
                                            {...register(channel.key)}
                                        />
                                    </div>
                                </div>
                            ))}

                            <div className="clinic-verification-send-grid">
                                <div className="clinic-verification-send-grid__title">Send Verification Request</div>
                                <p className="clinic-verification-hint">Trigger a verification request for each channel.</p>
                                {VERIFICATION_CHANNELS.map((channel) => (
                                    <button
                                        key={`send-${channel.key}`}
                                        type="button"
                                        className="btn btn-outline-success btn-sm"
                                        disabled={isSaving}
                                        onClick={() => sendVerification(channel.sendType)}
                                    >
                                        <i className={`bi ${channel.icon} me-1`}></i>
                                        Send {channel.label}
                                    </button>
                                ))}
                            </div>

                            <div className="clinic-verification-form-actions">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelEdit} disabled={isSaving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-success btn-sm" disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-floppy me-1"></i>
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className="clinic-verification-toolbar">
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="clinic-verification-stat clinic-verification-stat--total">3 channels</span>
                                    <span className="clinic-verification-stat clinic-verification-stat--verified">{verifiedCount} verified</span>
                                    <span className="clinic-verification-stat clinic-verification-stat--pending">{3 - verifiedCount} pending</span>
                                </div>
                            </div>

                            {VERIFICATION_CHANNELS.map((channel) => {
                                const verified = isVerified(clinicSettings[channel.key]);
                                return (
                                    <div key={channel.key} className="clinic-verification-row">
                                        <span className="clinic-verification-row__label">
                                            <span className={`clinic-verification-row__icon clinic-verification-row__icon--${channel.tone}`}>
                                                <i className={`bi ${channel.icon}`}></i>
                                            </span>
                                            {channel.label}
                                        </span>
                                        <span className={`clinic-verification-status-badge ${verified ? 'clinic-verification-status-badge--verified' : 'clinic-verification-status-badge--pending'}`}>
                                            <i className={`bi ${verified ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
                                            {verified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VerificationCard;
